import React, { useState } from 'react';
import { urlUtils } from '../utils/urlUtils';

const BookmarkCard = ({ bookmark, onDelete, onUpdate, onRefreshMetadata }) => {
  const [isEditing, setIsEditing] = useState(false);
  const [editData, setEditData] = useState({
    title: bookmark.title || '',
    description: bookmark.description || '',
    tags: bookmark.tags || []
  });
  const [tagInput, setTagInput] = useState('');

  const domain = urlUtils.extractDomain(bookmark.url);
  const createdDate = bookmark.createdAt?.toDate?.()?.toLocaleDateString() || 'Unknown date';

  const handleEdit = async () => {
    try {
      await onUpdate(bookmark.id, editData);
      setIsEditing(false);
    } catch (error) {
      console.error('Error updating bookmark:', error);
    }
  };

  const handleCancel = () => {
    setEditData({
      title: bookmark.title || '',
      description: bookmark.description || '',
      tags: bookmark.tags || []
    });
    setIsEditing(false);
  };

  const handleTagKeyPress = (e) => {
    if (e.key === 'Enter' || e.key === ',') {
      e.preventDefault();
      addTag();
    }
  };

  const addTag = () => {
    const tag = tagInput.trim();
    if (tag && !editData.tags.includes(tag)) {
      setEditData(prev => ({
        ...prev,
        tags: [...prev.tags, tag]
      }));
      setTagInput('');
    }
  };

  const removeTag = (tagToRemove) => {
    setEditData(prev => ({
      ...prev,
      tags: prev.tags.filter(tag => tag !== tagToRemove)
    }));
  };

  if (isEditing) {
    return (
      <div className="bg-teal-50 rounded-xl p-4 border-l-4 border-primary">
        <div className="space-y-3">
          <input
            type="text"
            value={editData.title}
            onChange={(e) => setEditData(prev => ({ ...prev, title: e.target.value }))}
            className="w-full text-base font-semibold px-3 py-2 bg-surface border border-border rounded-lg focus:outline-none focus:ring-2 focus:ring-primary focus:border-transparent transition-all duration-200"
            placeholder="Bookmark title"
          />
          
          <textarea
            value={editData.description}
            onChange={(e) => setEditData(prev => ({ ...prev, description: e.target.value }))}
            className="w-full px-3 py-2 bg-surface border border-border rounded-lg focus:outline-none focus:ring-2 focus:ring-primary focus:border-transparent transition-all duration-200 resize-none text-sm"
            placeholder="Description"
            rows={2}
          />

          <div>
            <input
              type="text"
              placeholder="Add tags (press Enter or comma)"
              value={tagInput}
              onChange={(e) => setTagInput(e.target.value)}
              onKeyPress={handleTagKeyPress}
              onBlur={addTag}
              className="w-full px-3 py-2 bg-surface border border-border rounded-lg focus:outline-none focus:ring-2 focus:ring-primary focus:border-transparent transition-all duration-200 text-sm"
            />
            {editData.tags.length > 0 && (
              <div className="flex flex-wrap gap-1 mt-2">
                {editData.tags.map((tag, index) => (
                  <span
                    key={index}
                    className="inline-flex items-center px-2 py-1 rounded-full text-xs bg-primary text-white font-medium"
                  >
                    {tag}
                    <button
                      type="button"
                      onClick={() => removeTag(tag)}
                      className="ml-1 text-white hover:text-gray-200 transition-colors"
                    >
                      ×
                    </button>
                  </span>
                ))}
              </div>
            )}
          </div>

          <div className="flex gap-2 pt-1">
            <button
              onClick={handleEdit}
              className="px-3 py-1.5 bg-success text-white rounded-lg text-sm font-medium hover:bg-emerald-700 transition-colors focus:outline-none focus:ring-2 focus:ring-success focus:ring-offset-2"
            >
              Save
            </button>
            <button
              onClick={handleCancel}
              className="px-3 py-1.5 bg-gray-100 text-gray-700 rounded-lg text-sm font-medium hover:bg-gray-200 transition-colors focus:outline-none focus:ring-2 focus:ring-gray-300 focus:ring-offset-2"
            >
              Cancel
            </button>
          </div>
        </div>
      </div>
    );
  }

  return (
    <div className="bg-teal-50 rounded-xl p-4 hover:shadow-lg transition-all duration-200 border border-border">
      {/* Header with title and action buttons */}
      <div className="flex items-start justify-between gap-3 mb-2">
        <div className="flex-1 min-w-0">
          <h3 className="text-base font-semibold text-gray-900 mb-1">
            <a
              href={bookmark.url}
              target="_blank"
              rel="noopener noreferrer"
              className="hover:text-primary transition-colors block truncate"
            >
              {bookmark.title || 'Untitled Bookmark'}
            </a>
          </h3>
        </div>
        
        {/* Action buttons - always visible, compact */}
        <div className="flex gap-1 flex-shrink-0">
          <button
            onClick={() => onRefreshMetadata(bookmark.id, bookmark.url)}
            className="p-1.5 text-gray-400 hover:text-blue-500 hover:bg-teal-50 rounded-lg transition-all duration-200"
            title="Refresh metadata"
          >
            <svg xmlns="http://www.w3.org/2000/svg" className="h-5 w-5" viewBox="0 0 20 20" fill="currentColor">
              <path fillRule="evenodd" d="M4 2a1 1 0 011 1v2.101a7.002 7.002 0 0111.601 2.566 1 1 0 11-1.885.666A5.002 5.002 0 005.999 7H9a1 1 0 010 2H4a1 1 0 01-1-1V3a1 1 0 011-1zm.008 9.057a1 1 0 011.276.61A5.002 5.002 0 0014.001 13H11a1 1 0 110-2h5a1 1 0 011 1v5a1 1 0 11-2 0v-2.101a7.002 7.002 0 01-11.601-2.566 1 1 0 01.61-1.276z" clipRule="evenodd" />
            </svg>
          </button>
          <button
            onClick={() => setIsEditing(true)}
            className="p-1.5 text-gray-400 hover:text-primary hover:bg-teal-50 rounded-lg transition-all duration-200"
            title="Edit bookmark"
          >
            <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 26 26">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M11 5H6a2 2 0 00-2 2v11a2 2 0 002 2h11a2 2 0 002-2v-5m-1.414-9.414a2 2 0 112.828 2.828L11.828 15H9v-2.828l8.586-8.586z" />
            </svg>
          </button>
          <button
            onClick={() => {
              if (window.confirm('Are you sure you want to delete this bookmark?')) {
                onDelete(bookmark.id);
              }
            }}
            className="p-1.5 text-gray-400 hover:text-white hover:bg-secondary rounded-lg transition-all duration-200"
            title="Delete bookmark"
          >
            <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 26 26">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M19 7l-.867 12.142A2 2 0 0116.138 21H7.862a2 2 0 01-1.995-1.858L5 7m5 4v6m4-6v6m1-10V4a1 1 0 00-1-1h-4a1 1 0 00-1 1v3M4 7h16" />
            </svg>
          </button>
          <a
            href={bookmark.url}
            target="_blank"
            rel="noopener noreferrer"
            className="p-1.5 text-gray-400 hover:text-accent transition-colors rounded-lg flex items-center"
            title="Visit bookmark"
          >
            <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 26 26">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M10 6H6a2 2 0 00-2 2v10a2 2 0 002 2h10a2 2 0 002-2v-4M14 4h6m0 0v6m0-6L10 14" />
            </svg>
          </a>
        </div>
      </div>

      {/* Domain, date, and tags - responsive layout */}
      <div className="text-xs text-gray-500 mb-2">
        {/* Mobile: Stack vertically */}
        <div className="sm:hidden">
          <div className="mb-1">
            <span className="truncate block">{domain} • {createdDate}</span>
          </div>
          {bookmark.tags && bookmark.tags.length > 0 && (
            <div className="flex gap-1 overflow-x-auto scrollbar-hide">
              {bookmark.tags.map((tag, index) => (
                <span
                  key={index}
                  className="px-2 py-0.5 bg-gray-100 text-gray-700 rounded-full text-xs whitespace-nowrap flex-shrink-0"
                >
                  {tag}
                </span>
              ))}
            </div>
          )}
        </div>

        {/* Desktop: Same row */}
        <div className="hidden sm:flex sm:items-center sm:justify-between sm:gap-3">
          <span className="truncate flex-shrink">{domain} • {createdDate}</span>
          {bookmark.tags && bookmark.tags.length > 0 && (
            <div className="flex gap-1 overflow-x-auto scrollbar-hide flex-shrink-0">
              {bookmark.tags.map((tag, index) => (
                <span
                  key={index}
                  className="px-2 py-0.5 bg-gray-100 text-gray-700 rounded-full text-xs whitespace-nowrap flex-shrink-0"
                >
                  {tag}
                </span>
              ))}
            </div>
          )}
        </div>
      </div>

      {/* Description */}
      {bookmark.description && (
        <p className="text-gray-600 text-sm mb-0">
          {bookmark.description.length > 250
            ? `${bookmark.description.substring(0, 250)}...`
            : bookmark.description}
        </p>
      )}
    </div>
  );
};

export default BookmarkCard;
